import React, { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, Typography } from '@mui/material';

interface SignaturePadProps {
  label: string;
  height?: number;
}

export interface SignaturePadRef {
  isEmpty: () => boolean;
  clear: () => void;
  toDataURL: () => string;
}

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(({ label, height = 150 }, ref) => {
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const savedDataRef = useRef<string | null>(null);

  // Save signature data whenever a stroke ends
  const handleEnd = useCallback(() => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      savedDataRef.current = sigCanvasRef.current.toDataURL();
    }
  }, []);

  // Restore signature after canvas resize
  const restoreSignature = useCallback(() => {
    if (savedDataRef.current && sigCanvasRef.current) {
      // Small delay to let canvas finish resizing
      setTimeout(() => {
        if (sigCanvasRef.current && savedDataRef.current) {
          sigCanvasRef.current.fromDataURL(savedDataRef.current, {
            ratio: 1,
            width: containerRef.current?.offsetWidth || 300,
            height: height,
          });
        }
      }, 50);
    }
  }, [height]);

  useEffect(() => {
    // Listen for resize events (mobile address bar show/hide, orientation change)
    const handleResize = () => {
      restoreSignature();
    };

    // Use ResizeObserver for the container specifically
    const observer = new ResizeObserver(() => {
      restoreSignature();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Also handle scroll-based canvas clearing on iOS
    // iOS Safari can clear canvas when it goes off-screen
    const handleScroll = () => {
      if (savedDataRef.current && sigCanvasRef.current?.isEmpty()) {
        restoreSignature();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [restoreSignature]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    isEmpty: () => {
      // Check both live canvas and saved data
      if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) return false;
      return !savedDataRef.current;
    },
    clear: () => {
      sigCanvasRef.current?.clear();
      savedDataRef.current = null;
    },
    toDataURL: () => {
      // Return saved data if canvas was cleared by browser
      if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
        return sigCanvasRef.current.toDataURL();
      }
      return savedDataRef.current || '';
    },
  }));

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        {label}
      </Typography>
      <Box
        ref={containerRef}
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          touchAction: 'none', // Prevents scroll while signing
          position: 'relative',
        }}
      >
        <SignatureCanvas
          ref={sigCanvasRef}
          onEnd={handleEnd}
          canvasProps={{
            className: 'signature-canvas',
            style: {
              width: '100%',
              height: `${height}px`,
              touchAction: 'none', // Critical for mobile - prevents scroll interference
            },
          }}
          backgroundColor="#fff"
        />
      </Box>
      <Button
        size="small"
        variant="outlined"
        sx={{ mt: 1 }}
        onClick={() => {
          sigCanvasRef.current?.clear();
          savedDataRef.current = null;
        }}
      >
        Clear
      </Button>
    </Box>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
